import { useId } from 'react';
import { cn } from '@/lib/utils';

export interface PayoffBarProps {
  longPut: number | null;
  shortPut: number | null;
  shortCall: number | null;
  longCall: number | null;
  price: number | null;
  /** Net credit per share for the merged open legs (learnCtx.credit); null -> no drawing. */
  credit: number | null;
  /** From the board's Greeks fetch; null until it lands or when a leg cannot be priced. */
  odds: { putProb: number | null; callProb: number | null } | null;
  /** Expected move to expiration in dollars, or null. */
  expectedMove: number | null;
}

const W = 640;
const PLOT_TOP = 16;
const PLOT_H = 56;
/** The plot's bottom edge and the axis the ticks and the expected-move band sit on. */
const AXIS_Y = PLOT_TOP + PLOT_H;

// Colour means money. SVG attributes take no Tailwind classes, so these are the literal values
// behind the `profit`/`loss` tokens in tailwind.config.js; the HTML layer uses the classes.
const GOOD = '#16a34a';
const BAD = '#dc2626';
const GOOD_SOFT = '#dcfce7';
const BAD_SOFT = '#fee2e2';
const INK = '#374151';
const MUTED = '#9ca3af';
const ACCENT = '#0ea5e9';
/** Half the gap between two coincident short labels, in viewBox units. */
const LABEL_NUDGE = 14;

interface Domain {
  lo: number;
  hi: number;
}

/**
 * The horizontal scale, carried over unchanged from the strike bar this replaces.
 *
 * Each end gets room to sit a breached price on before the clamp arrow, rather than a fixed
 * percentage of the span: a wing's own width on the side that has one (`Math.abs`'d so an
 * inverted wing still yields positive room), or a tenth of that side's own short strike when
 * there is no wing to size it from. A one-sided position runs the empty side out past the price
 * by one strike-span, so it reads as "the other side isn't here" rather than cramming everything
 * into its own width.
 */
function computeDomain(
  longPut: number | null,
  shortPut: number | null,
  shortCall: number | null,
  longCall: number | null,
  price: number | null,
): Domain {
  const strikes = [longPut, shortPut, shortCall, longCall].filter((v): v is number => v !== null);
  const lo0 = Math.min(...strikes);
  const hi0 = Math.max(...strikes);

  const roomLeft =
    longPut !== null && shortPut !== null ? Math.abs(shortPut - longPut) : 0.1 * (shortPut ?? lo0);
  const roomRight =
    longCall !== null && shortCall !== null ? Math.abs(longCall - shortCall) : 0.1 * (shortCall ?? hi0);

  if (shortPut !== null && shortCall !== null) {
    const lo = lo0 - roomLeft;
    const hi = hi0 + roomRight;
    if (hi - lo <= 0) return { lo: lo0 - 0.1 * lo0, hi: hi0 + 0.1 * hi0 };
    return { lo, hi };
  }

  if (shortCall === null) {
    // One-sided put. `shortPut` is non-null here — the component returns null before this point
    // when both shorts are null.
    const span = hi0 - lo0 > 0 ? hi0 - lo0 : 0.1 * hi0;
    return { lo: lo0 - roomLeft, hi: Math.max(hi0, price ?? hi0) + span };
  }

  const span = hi0 - lo0 > 0 ? hi0 - lo0 : 0.1 * hi0;
  return { lo: Math.min(lo0, price ?? lo0) - span, hi: hi0 + roomRight };
}

type Legs = Pick<PayoffBarProps, 'longPut' | 'shortPut' | 'shortCall' | 'longCall'>;

/**
 * Settlement per share at price S:
 *   credit - max(0, shortPut - S) + max(0, longPut - S)
 *          - max(0, S - shortCall) + max(0, S - longCall)
 * Each term is present only for a leg that exists.
 */
function payoffAt(S: number, legs: Legs, credit: number): number {
  let value = credit;
  if (legs.shortPut !== null) value -= Math.max(0, legs.shortPut - S);
  if (legs.longPut !== null) value += Math.max(0, legs.longPut - S);
  if (legs.shortCall !== null) value -= Math.max(0, S - legs.shortCall);
  if (legs.longCall !== null) value += Math.max(0, S - legs.longCall);
  return value;
}

/** Strip trailing zeros: 95 -> "95", 102.5 -> "102.5". */
const formatStrike = (value: number) => String(parseFloat(value.toFixed(2)));
/** U+2212 for a negative, so the label reads as money rather than as a hyphenated range. */
const signed = (value: number) => `${value >= 0 ? '+' : '−'}${Math.abs(value).toFixed(2)}`;

function chipClass(prob: number | null): string {
  if (prob === null) return 'bg-gray-100 text-gray-500';
  if (prob < 15) return 'bg-green-100 text-green-700';
  if (prob <= 30) return 'bg-amber-100 text-amber-700';
  return 'bg-red-100 text-red-700';
}

/** The Greeks fetch returns a fractional probability; the chip always reads a whole percent. */
const roundPct = (value: number | null) => (value === null ? null : Math.round(value));

const chipAriaLabel = (pct: number | null) =>
  pct === null ? 'unknown chance of finishing in the money' : `${pct}% chance of finishing in the money`;

type LabelAnchor = 'left' | 'center' | 'right';

/**
 * Where a price-anchored element (the settlement label, its clamp arrow) should pin itself along
 * the track, so a wide element never hangs off the edge it is closest to. Below 20% of the track
 * the element's own left edge sits at the marker; above 80% its right edge does (a ~115px label
 * centred at 10% of a 320px phone track would still hang 25px past it); the rest of the
 * track centres on the marker as before.
 */
function labelAnchor(pct: number): LabelAnchor {
  if (pct < 20) return 'left';
  if (pct > 80) return 'right';
  return 'center';
}

function anchorTransform(anchor: LabelAnchor): string {
  if (anchor === 'left') return 'translateX(0%)';
  if (anchor === 'right') return 'translateX(-100%)';
  return 'translateX(-50%)';
}

function ariaLabel(legs: Legs, price: number | null, settlement: number | null): string {
  const parts: string[] = [];
  if (legs.shortPut !== null) {
    parts.push(
      `Put ${legs.longPut !== null ? `${formatStrike(legs.longPut)}/` : ''}${formatStrike(legs.shortPut)}`,
    );
  }
  if (legs.shortCall !== null) {
    parts.push(
      `call ${formatStrike(legs.shortCall)}${legs.longCall !== null ? `/${formatStrike(legs.longCall)}` : ''}`,
    );
  }
  if (price === null || settlement === null) {
    parts.push('no quote');
  } else {
    parts.push(`price ${price.toFixed(2)}`);
    parts.push(`settles ${signed(settlement)} per share at that price`);
  }
  return parts.join(', ');
}

/**
 * The iron condor's payoff at expiration under a Risk view card's strikes line: flat credit
 * between the shorts, ramping through the wings, flat maximum loss beyond. Colour means money —
 * green above zero, red below. Props-driven from the row's own merged open legs: no lookups, no
 * state, no fetching.
 */
export function PayoffBar({
  longPut,
  shortPut,
  shortCall,
  longCall,
  price,
  credit,
  odds,
  expectedMove,
}: PayoffBarProps): JSX.Element | null {
  const uid = useId().replace(/:/g, '');

  // Long-only: nothing to watch. No credit: there is no profile to draw.
  if (shortPut === null && shortCall === null) return null;
  if (credit === null || !Number.isFinite(credit)) return null;

  const legs: Legs = { longPut, shortPut, shortCall, longCall };
  const domain = computeDomain(longPut, shortPut, shortCall, longCall, price);
  const span = domain.hi - domain.lo;

  const pl = (S: number) => payoffAt(S, legs, credit);
  const lossAtLo = pl(domain.lo);
  const lossAtHi = pl(domain.hi);

  // The edge form of the max loss. Identical to `credit - max(putWidth, callWidth)` wherever a
  // wing exists, and still defined when one does not.
  const maxLoss = Math.min(lossAtLo, lossAtHi);
  const yHi = Math.max(1.4 * credit, 0.01);
  const yLo = Math.min(1.1 * maxLoss, -0.1 * yHi);
  const yRange = yHi - yLo;

  const rawX = (S: number) => ((S - domain.lo) / span) * W;
  const x = (S: number) => Math.min(W, Math.max(0, rawX(S)));
  const y = (value: number) => PLOT_TOP + ((yHi - value) / yRange) * PLOT_H;
  /**
   * The dot is the one mark whose value is not the drawing's own: a naked short priced far past
   * the domain settles many multiples below `yLo`, which puts its `cy` outside the viewBox
   * entirely and the dot simply disappears. Pinned to the band's edge it still reads as "off the
   * bottom of the scale", which is the true statement.
   */
  const yClamped = (value: number) => Math.min(AXIS_Y, Math.max(PLOT_TOP, y(value)));

  const zeroY = y(0);
  const strikes = [longPut, shortPut, shortCall, longCall]
    .filter((v): v is number => v !== null)
    .sort((a, b) => a - b);
  const curve = [domain.lo, ...strikes, domain.hi].map((S) => [x(S), y(pl(S))] as const);
  const points = curve.map(([px, py]) => `${px.toFixed(3)},${py.toFixed(3)}`).join(' ');
  const area = [
    `M ${curve[0][0].toFixed(3)},${curve[0][1].toFixed(3)}`,
    ...curve.slice(1).map(([px, py]) => `L ${px.toFixed(3)},${py.toFixed(3)}`),
    `L ${curve[curve.length - 1][0].toFixed(3)},${zeroY.toFixed(3)}`,
    `L ${curve[0][0].toFixed(3)},${zeroY.toFixed(3)}`,
    'Z',
  ].join(' ');

  const settlement = price !== null ? pl(price) : null;
  const priceRaw = price !== null ? rawX(price) : null;
  const clampedLeft = priceRaw !== null && priceRaw < 0;
  const clampedRight = priceRaw !== null && priceRaw > W;
  const priceX = price !== null ? x(price) : null;
  const priceColor = settlement !== null && settlement >= 0 ? GOOD : BAD;

  const bandFrom = price !== null && expectedMove !== null ? x(price - expectedMove) : null;
  const bandTo = price !== null && expectedMove !== null ? x(price + expectedMove) : null;

  const ticks = [
    longPut !== null ? { key: 'longPut', value: longPut, short: false } : null,
    shortPut !== null ? { key: 'shortPut', value: shortPut, short: true } : null,
    shortCall !== null ? { key: 'shortCall', value: shortCall, short: true } : null,
    longCall !== null ? { key: 'longCall', value: longCall, short: false } : null,
  ].filter((t): t is { key: string; value: number; short: boolean } => t !== null);

  const pct = (value: number) => (x(value) / W) * 100;

  /**
   * A straddle (and a strangle a hair wide) puts both short labels and both chips on one x, where
   * they overlap into an unreadable stack. When the shorts sit within 1% of the domain width of
   * each other the put label moves left and the call label right by `LABEL_NUDGE`. Only the
   * labels move — the ticks stay on the strikes they mark, which really are at the same price.
   */
  const shortsCoincide =
    shortPut !== null && shortCall !== null && Math.abs(shortCall - shortPut) <= 0.01 * span;
  const labelNudge = (key: string) => {
    if (!shortsCoincide) return 0;
    if (key === 'shortPut') return -LABEL_NUDGE;
    if (key === 'shortCall') return LABEL_NUDGE;
    return 0;
  };
  const labelPct = (key: string, value: number) => pct(value) + (labelNudge(key) / W) * 100;

  // Each side's own worst case at its own domain edge. A short with no long ramps on to the edge
  // and beyond, so it reads "unlimited" rather than a number that depends on where the scale ends.
  const putMaxLoss =
    shortPut === null ? null : longPut === null ? 'unlimited' : `${signed(lossAtLo)}/sh`;
  const callMaxLoss =
    shortCall === null ? null : longCall === null ? 'unlimited' : `${signed(lossAtHi)}/sh`;

  // The label and its clamp arrow anchor away from whichever edge they're pinned to; the caption
  // moves off the label's own corner so the two never overlap at that same edge.
  const priceAnchor = price !== null ? labelAnchor(pct(price)) : 'center';
  const captionOnLeft = price !== null && priceAnchor === 'right';

  const putPct = roundPct(odds?.putProb ?? null);
  const callPct = roundPct(odds?.callProb ?? null);

  return (
    <div data-testid="payoff-bar" className="relative mx-2">
      {/* Top row: what the position settles at right now, and the expected move caption. */}
      <div className="relative h-4 text-[10px]">
        <span
          data-testid="payoff-price-label"
          className={cn(
            'absolute whitespace-nowrap font-mono',
            price === null
              ? 'text-gray-400'
              : settlement !== null && settlement >= 0
                ? 'text-green-600'
                : 'text-red-600',
          )}
          style={
            price === null
              ? { left: '0%' }
              : { left: `${pct(price).toFixed(3)}%`, transform: anchorTransform(priceAnchor) }
          }
        >
          {price === null || settlement === null
            ? 'no quote'
            : `${signed(settlement)}/sh at ${price.toFixed(2)}`}
        </span>
        {(clampedLeft || clampedRight) && (
          <span
            data-testid="payoff-price-clamped"
            className="absolute text-gray-500"
            style={{
              left: `${pct(price!).toFixed(3)}%`,
              // Matches the StrikeBar precedent this replaces: the arrow anchors on the opposite
              // side from the direction it points, not a fixed offset past the marker.
              transform: clampedLeft ? 'translateX(-100%)' : 'translateX(0%)',
            }}
          >
            {clampedLeft ? '◂' : '▸'}
          </span>
        )}
        {expectedMove !== null && (
          <span
            data-testid="payoff-em-caption"
            className={cn('absolute text-gray-400 font-mono', captionOnLeft ? 'left-0' : 'right-0')}
          >
            ±{expectedMove.toFixed(2)} by expiry
          </span>
        )}
      </div>

      <svg
        role="img"
        aria-label={ariaLabel(legs, price, settlement)}
        viewBox={`0 0 ${W} 96`}
        width="100%"
        preserveAspectRatio="none"
        className="block"
      >
        <defs>
          <clipPath id={`${uid}-above`}>
            <rect x="0" y={PLOT_TOP} width={W} height={Math.max(0, zeroY - PLOT_TOP)} />
          </clipPath>
          <clipPath id={`${uid}-below`}>
            <rect x="0" y={zeroY} width={W} height={Math.max(0, AXIS_Y - zeroY)} />
          </clipPath>
        </defs>

        <path data-testid="payoff-area-good" d={area} fill={GOOD_SOFT} clipPath={`url(#${uid}-above)`} />
        <path data-testid="payoff-area-bad" d={area} fill={BAD_SOFT} clipPath={`url(#${uid}-below)`} />

        <line
          data-testid="payoff-zero"
          x1={0}
          x2={W}
          y1={zeroY}
          y2={zeroY}
          stroke={MUTED}
          strokeWidth={1}
          strokeDasharray="4 4"
        />

        <polyline
          data-testid="payoff-curve"
          points={points}
          fill="none"
          stroke={INK}
          strokeWidth={2}
          strokeLinejoin="round"
        />

        {bandFrom !== null && bandTo !== null && (
          <rect
            data-testid="payoff-em-band"
            x={Math.min(bandFrom, bandTo)}
            y={AXIS_Y}
            width={Math.abs(bandTo - bandFrom)}
            height={6}
            fill={ACCENT}
            opacity={0.18}
          />
        )}

        {ticks.map((tick) => (
          <line
            key={tick.key}
            data-testid={`payoff-tick-${tick.key}`}
            x1={x(tick.value)}
            x2={x(tick.value)}
            y1={AXIS_Y}
            y2={AXIS_Y + 8}
            stroke={tick.short ? INK : MUTED}
            strokeWidth={tick.short ? 1.5 : 1}
          />
        ))}

        {priceX !== null && (
          <line
            data-testid="payoff-price-line"
            x1={priceX}
            x2={priceX}
            y1={PLOT_TOP}
            y2={AXIS_Y}
            stroke={priceColor}
            strokeWidth={1.5}
          />
        )}
        {priceX !== null && settlement !== null && (
          <circle
            data-testid="payoff-price-dot"
            cx={priceX}
            cy={yClamped(settlement)}
            r={3.5}
            fill={priceColor}
          />
        )}
      </svg>

      {/* Bottom row: strike labels with their odds chips, and each side's max loss at the edges. */}
      <div className="relative h-8">
        {ticks.map((tick) => (
          <div
            key={tick.key}
            data-testid={`payoff-label-${tick.key}`}
            className="absolute top-0 flex flex-col items-center"
            style={{ left: `${labelPct(tick.key, tick.value).toFixed(3)}%`, transform: 'translateX(-50%)' }}
          >
            <span
              data-testid={`payoff-strike-${tick.key}`}
              aria-hidden="true"
              className={cn(
                'text-[10px] font-mono whitespace-nowrap',
                tick.short ? 'text-gray-700' : 'text-gray-400',
              )}
            >
              {formatStrike(tick.value)}
            </span>
            {odds !== null && tick.key === 'shortPut' && (
              <span
                data-testid="payoff-chip-put"
                role="img"
                aria-label={chipAriaLabel(putPct)}
                className={cn('mt-0.5 rounded px-1 text-[9px] font-medium', chipClass(putPct))}
              >
                {putPct === null ? '—' : `${putPct}%`}
              </span>
            )}
            {odds !== null && tick.key === 'shortCall' && (
              <span
                data-testid="payoff-chip-call"
                role="img"
                aria-label={chipAriaLabel(callPct)}
                className={cn('mt-0.5 rounded px-1 text-[9px] font-medium', chipClass(callPct))}
              >
                {callPct === null ? '—' : `${callPct}%`}
              </span>
            )}
          </div>
        ))}
        {putMaxLoss !== null && (
          <span data-testid="payoff-maxloss-put" className="absolute left-0 bottom-0 text-[9px] font-mono text-gray-400">
            {putMaxLoss}
          </span>
        )}
        {callMaxLoss !== null && (
          <span data-testid="payoff-maxloss-call" className="absolute right-0 bottom-0 text-[9px] font-mono text-gray-400">
            {callMaxLoss}
          </span>
        )}
      </div>
    </div>
  );
}
